import {
    A365Named,
    Folder,
    IDataFlowExpression,
    MappingType,
    Metamodel,
    ModelConstants,
    Owned
} from 'shell-models-lib';
import { MapperAttributeReference } from 'app/model/adfDataMapper/mapperAttributeReference';
import { MapperConnectionReference } from 'app/model/adfDataMapper/mapperConnectionReference';
import { IMapperTable } from 'app/model/adfDataMapper/IMapperTable';

export interface IMapperLookupInfo {
    entity: string;
    connectionReference: MapperConnectionReference;
    attribute: string;
    joinPairs: IMapperJoinPair[];
}

export interface IMapperJoinPair {
    fromAttribute: MapperAttributeReference;
    toAttribute: string;
    operator?: string;
    expression?: string;
}

export interface IMapperUnpivotInfo {
    targetUnpivotKeyAttribute: string;
    sourceUnpivotAttributes: IMapperUnpivotAttribute[];
}

export interface IMapperUnpivotAttribute {
    unpivotKeyValue: string;
    attributeReference: MapperAttributeReference;
}

@Metamodel.model({
    name: ModelConstants.adfDataMapperAttributeMappings,
    display: [ stringResources.Attributemapping, stringResources.Attributemappings ],
    reusable: true,
    referable: true
})
export class MapperAttributeMapping extends A365Named {
    public name: string = '';
    public mappingType: string = MappingType.DIRECT;
    public attributeReference: MapperAttributeReference;
    public attributeReferences: MapperAttributeReference[];
    public expression: IDataFlowExpression;
    public alternateKeys: string[] = [];
    public unpivotInfo: IMapperUnpivotInfo;
    public lookupFrom: IMapperLookupInfo;
    public functionName: string;
    public isSystemGenerated: boolean = false;
    public isPrimaryKeyColumn: boolean = false;
    public isRecommended: boolean = false;

    constructor(name: string, mappingType?: string) {
        super();
        this.resetMappingInfo();
        this.folder = Owned.create(Folder.MODEL, undefined);
        this.name = name;
        this.mappingType = mappingType;
    }

    public setName(name: string): void {
        this.name = name;
    }

    public setMappingType(newMappingType: MappingType): void {
        if (this._shouldResetMappingInfo(this.mappingType, newMappingType)) {
            this.resetMappingInfo();
        }

        this.mappingType = newMappingType;
    }

    public resetMappingInfo(): void {
        this.attributeReference = new MapperAttributeReference('', undefined, undefined);
        this.attributeReferences = [];
        this.expression = undefined;
        this.alternateKeys = [];
        this.unpivotInfo = undefined;
        this.functionName = '';
    }

    public setAttributeReference(colName: string, table: IMapperTable, connectionReference: MapperConnectionReference): void {
        this.attributeReference = new MapperAttributeReference(colName, table, connectionReference);
    }

    public addAttributeReference(colName: string, table: IMapperTable, connectionReference: MapperConnectionReference): void {
        this.attributeReferences.push(new MapperAttributeReference(colName, table, connectionReference));
    }

    public removeAttributeReference(colName: string, entity: string): void {
        this.attributeReferences = this.attributeReferences.filter((attRef) => attRef.columnName !== colName || attRef.table?.tableName !== entity);
    }

    public setExpression(expression: IDataFlowExpression): void {
        this.expression = expression;
    }

    public setFunction(name: string): void {
        this.functionName = name;
    }

    private _shouldResetMappingInfo(previousMappingType: string, newMappingType: MappingType): boolean {
        if (newMappingType === MappingType.LOOKUP || newMappingType === MappingType.SURROGATEKEY || newMappingType === MappingType.UNPIVOT) {
            return true;
        }
        if (previousMappingType === MappingType.ADVANCED && previousMappingType !== newMappingType) {
            return true;
        }

        return false;
    }
}
